import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrApi, hrAttendanceApi, type PersonnelFileRow } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";

export function AttendanceTab() {
  const [selectedDate, setSelectedDate] = useState(tehranToday());

  // Modal states
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [selectedPersonForCode, setSelectedPersonForCode] = useState<PersonnelFileRow | null>(null);
  const [newAttendanceCode, setNewAttendanceCode] = useState("");

  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [corrPersonId, setCorrPersonId] = useState("");
  const [corrPunchType, setCorrPunchType] = useState<"in" | "out">("in");
  const [corrTime, setCorrTime] = useState("08:00");
  const [corrReason, setCorrReason] = useState("");

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leavePersonId, setLeavePersonId] = useState("");
  const [leaveKind, setLeaveKind] = useState<"daily" | "hourly" | "sick" | "unpaid" | "other">("daily");
  const [leaveStartDate, setLeaveStartDate] = useState(selectedDate);
  const [leaveEndDate, setLeaveEndDate] = useState(selectedDate);
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveReplacementId, setLeaveReplacementId] = useState("");

  const staffQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const attendanceQuery = useQuery({
    queryKey: ["hr", "attendance-today", selectedDate],
    queryFn: () => hrAttendanceApi.getTodayAttendance(selectedDate),
  });

  const correctionsQuery = useQuery({
    queryKey: ["hr", "punch-corrections", selectedDate],
    queryFn: () => hrAttendanceApi.listPunchCorrections(selectedDate, "pending"),
  });

  const setCodeAction = useAction(async () => {
    if (!selectedPersonForCode) return;
    await hrAttendanceApi.setAttendanceCode(selectedPersonForCode.personId, newAttendanceCode.trim());
    setShowCodeModal(false);
    setSelectedPersonForCode(null);
    setNewAttendanceCode("");
  }, [["hr", "attendance-today"]]);

  const proposeCorrectionAction = useAction(async () => {
    await hrAttendanceApi.proposePunchCorrection({
      personId: corrPersonId,
      date: selectedDate,
      punchType: corrPunchType,
      proposedTime: corrTime,
      reason: corrReason.trim(),
    });
    setShowCorrectionModal(false);
    setCorrReason("");
  }, [
    ["hr", "attendance-today"],
    ["hr", "punch-corrections"],
  ]);

  const reviewCorrectionAction = useAction(
    async ({ id, status }: { id: string; status: "approved" | "rejected" }) => {
      await hrAttendanceApi.reviewPunchCorrection(id, {
        status,
        reviewNote: status === "approved" ? "تأیید توسط مدیر" : "رد توسط مدیر",
      });
    },
    [
      ["hr", "attendance-today"],
      ["hr", "punch-corrections"],
    ],
  );

  const recordLeaveAction = useAction(async () => {
    await hrAttendanceApi.recordLeave({
      personId: leavePersonId,
      kind: leaveKind,
      startDate: leaveStartDate,
      endDate: leaveEndDate,
      startTime: null,
      endTime: null,
      reason: leaveReason.trim(),
      replacementPersonId: leaveReplacementId ? leaveReplacementId : null,
    });
    setShowLeaveModal(false);
    setLeaveReason("");
  }, [
    ["hr", "attendance-today"],
    ["hr", "leaves"],
  ]);

  const runUnderstaffedAction = useAction(async () => {
    const res = await hrAttendanceApi.runUnderstaffedJob(selectedDate);
    if (res.alertedShifts.length > 0) {
      alert(`هشدار کمبود پرسنل برای شیفت‌های: ${res.alertedShifts.join("، ")} ارسال شد.`);
    } else {
      alert("کمبود پرسنلی در شیفت‌های این روز یافت نشد.");
    }
  }, []);

  const summary = attendanceQuery.data;
  const staff: PersonnelFileRow[] = staffQuery.data ?? [];
  const pendingCorrections = correctionsQuery.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* ---------- Header / Controls ---------- */}
      <Panel title="گزارش حضور و غیاب امروز">
        <div
          className="row wrap"
          style={{ justifyContent: "space-between", alignItems: "center", gap: "var(--s3)" }}
        >
          <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
            <label className="label" htmlFor="filter-attendance-date">
              تاریخ:
            </label>
            <input
              id="filter-attendance-date"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
              }}
              style={{ padding: "6px 12px", borderRadius: "6px" }}
            />
          </div>

          <div className="row" style={{ gap: "var(--s2)" }}>
            <Button
              variant="primary"
              onClick={() => {
                const first = staff[0];
                if (first && !leavePersonId) setLeavePersonId(first.personId);
                setShowLeaveModal(true);
              }}
            >
              ثبت مرخصی
            </Button>
            <Button
              onClick={() => {
                runUnderstaffedAction.run(undefined);
              }}
              disabled={runUnderstaffedAction.busy}
            >
              بررسی کمبود پرسنل
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        {summary && (
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
              gap: "var(--s3)",
              marginTop: "var(--s3)",
            }}
          >
            <div className="card" style={{ padding: "var(--s3)", textAlign: "center" }}>
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                کل موظفین
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>{summary.plannedCount}</div>
            </div>
            <div
              className="card"
              style={{ padding: "var(--s3)", textAlign: "center", borderColor: "var(--accent)" }}
            >
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                حاضرین
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--accent)" }}>
                {summary.presentCount}
              </div>
            </div>
            <div className="card" style={{ padding: "var(--s3)", textAlign: "center" }}>
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                با تأخیر
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#f59e0b" }}>
                {summary.lateCount}
              </div>
            </div>
            <div className="card" style={{ padding: "var(--s3)", textAlign: "center" }}>
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                غایبین
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--danger)" }}>
                {summary.absentCount}
              </div>
            </div>
            <div className="card" style={{ padding: "var(--s3)", textAlign: "center" }}>
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                مرخصی
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>{summary.onLeaveCount}</div>
            </div>
            <div
              className="card"
              style={{ padding: "var(--s3)", textAlign: "center", borderColor: "var(--danger)" }}
            >
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                تردد ناقص
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--danger)" }}>
                {summary.missingPunchCount}
              </div>
            </div>
          </div>
        )}
      </Panel>

      {/* ---------- Pending Corrections (Approval by Manager) ---------- */}
      {pendingCorrections.length > 0 && (
        <Panel title={`درخواست‌های تصحیح تردد در انتظار تأیید (${String(pendingCorrections.length)})`}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام پرسنل</th>
                  <th>تاریخ</th>
                  <th>نوع تردد</th>
                  <th>ساعت پیشنهادی</th>
                  <th>علت</th>
                  <th>عملیات تأیید</th>
                </tr>
              </thead>
              <tbody>
                {pendingCorrections.map((corr) => (
                  <tr key={corr.id}>
                    <td>
                      <strong>{corr.personName}</strong>
                    </td>
                    <td>{corr.date}</td>
                    <td>{corr.punchType === "in" ? "ورود" : "خروج"}</td>
                    <td>
                      <strong>{corr.proposedTime}</strong>
                    </td>
                    <td className="muted">{corr.reason}</td>
                    <td>
                      <div className="row" style={{ gap: "var(--s2)" }}>
                        <Button
                          variant="primary"
                          onClick={() => {
                            reviewCorrectionAction.run({ id: corr.id, status: "approved" });
                          }}
                        >
                          تأیید تردد
                        </Button>
                        <Button
                          variant="urgent"
                          onClick={() => {
                            reviewCorrectionAction.run({ id: corr.id, status: "rejected" });
                          }}
                        >
                          رد
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* ---------- Daily Attendance Table ---------- */}
      <Panel title="لیست وضعیت حضور پرسنل">
        {!summary || summary.items.length === 0 ? (
          <p className="muted">اطلاعاتی برای نمایش وجود ندارد.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام پرسنل</th>
                  <th>سمت</th>
                  <th>شیفت کاری</th>
                  <th>اولین ورود</th>
                  <th>آخرین خروج</th>
                  <th>وضعیت</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {summary.items.map((item) => {
                  let badge: React.ReactNode;
                  if (item.status === "present") {
                    badge = (
                      <div
                        className="row"
                        style={{ gap: "var(--s1)", display: "inline-flex", alignItems: "center" }}
                      >
                        <span className="chip" style={{ color: "var(--accent)" }}>
                          حاضر
                        </span>
                        {item.isLate && (
                          <span className="chip" style={{ color: "#f59e0b" }}>
                            با تأخیر
                          </span>
                        )}
                      </div>
                    );
                  } else if (item.status === "late") {
                    badge = (
                      <span className="chip" style={{ color: "#f59e0b" }}>
                        با تأخیر
                      </span>
                    );
                  } else if (item.status === "absent") {
                    badge = (
                      <span className="chip" style={{ color: "var(--danger)" }}>
                        غایب
                      </span>
                    );
                  } else if (item.status === "missing_punch") {
                    badge = (
                      <span
                        className="chip"
                        style={{ backgroundColor: "#fef2f2", color: "#dc2626", fontWeight: "bold" }}
                      >
                        تردد ناقص
                      </span>
                    );
                  } else if (item.status === "on_leave") {
                    badge = (
                      <span className="chip" style={{ color: "#8b5cf6" }}>
                        مرخصی
                      </span>
                    );
                  } else {
                    badge = <span className="chip muted">تعطیل</span>;
                  }

                  const pFile = staff.find((s: PersonnelFileRow) => s.personId === item.personId);

                  return (
                    <tr key={item.personId}>
                      <td>
                        <strong>{item.personName}</strong>
                      </td>
                      <td className="muted">{item.positionName || "—"}</td>
                      <td>
                        {item.plannedShift.isOff ? (
                          <span className="muted">تعطیل</span>
                        ) : (
                          `${item.plannedShift.shiftName} (${item.plannedShift.startTime || ""} تا ${item.plannedShift.endTime || ""})`
                        )}
                      </td>
                      <td>
                        {item.firstIn
                          ? new Date(item.firstIn).toLocaleTimeString("fa-IR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td>
                        {item.lastOut
                          ? new Date(item.lastOut).toLocaleTimeString("fa-IR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td>{badge}</td>
                      <td>
                        <div className="row" style={{ gap: "var(--s2)" }}>
                          <Button
                            onClick={() => {
                              setCorrPersonId(item.personId);
                              setCorrPunchType(item.firstIn ? "out" : "in");
                              setShowCorrectionModal(true);
                            }}
                          >
                            تصحیح تردد
                          </Button>
                          <Button
                            onClick={() => {
                              if (pFile) setSelectedPersonForCode(pFile);
                              setShowCodeModal(true);
                            }}
                          >
                            کد حضور
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal: Set Attendance Code */}
      {showCodeModal && selectedPersonForCode && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: "400px" }}>
            <header className="modal-header">
              <h3>تنظیم کد حضور (PIN) پرسنل</h3>
            </header>
            <div className="modal-body stack" style={{ gap: "var(--s3)" }}>
              <p>
                تنظیم کد تردد تبلت برای: <strong>{selectedPersonForCode.personName}</strong>
              </p>
              <Field
                id="attendance-code-input"
                label="کد حضور ۴ تا ۶ رقمی"
                type="password"
                maxLength={6}
                value={newAttendanceCode}
                onChange={(e) => {
                  setNewAttendanceCode(e.target.value.replace(/\D/g, ""));
                }}
                placeholder="مثال: ۱۲۳۴"
                style={{ textAlign: "center", fontSize: "1.2rem", letterSpacing: "4px" }}
              />
              <ErrorLine error={setCodeAction.error} />
            </div>
            <footer className="modal-footer row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowCodeModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={newAttendanceCode.length < 4 || setCodeAction.busy}
                onClick={() => {
                  setCodeAction.run(undefined);
                }}
              >
                ثبت کد حضور
              </Button>
            </footer>
          </div>
        </div>
      )}

      {/* Modal: Propose Punch Correction */}
      {showCorrectionModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: "450px" }}>
            <header className="modal-header">
              <h3>ثبت پیشنهاد تصحیح تردد</h3>
            </header>
            <div className="modal-body stack" style={{ gap: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="corr-person-select" className="label">
                  پرسنل
                </label>
                <select
                  id="corr-person-select"
                  className="input"
                  value={corrPersonId}
                  onChange={(e) => {
                    setCorrPersonId(e.target.value);
                  }}
                >
                  {staff.map((s: PersonnelFileRow) => (
                    <option key={s.personId} value={s.personId}>
                      {s.personName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="corr-punch-type" className="label">
                  نوع تردد
                </label>
                <select
                  id="corr-punch-type"
                  className="input"
                  value={corrPunchType}
                  onChange={(e) => {
                    setCorrPunchType(e.target.value as "in" | "out");
                  }}
                >
                  <option value="in">ورود</option>
                  <option value="out">خروج</option>
                </select>
              </div>

              <Field
                id="corr-time-input"
                label="ساعت دقیق (HH:mm)"
                type="time"
                value={corrTime}
                onChange={(e) => {
                  setCorrTime(e.target.value);
                }}
              />

              <Field
                id="corr-reason-input"
                label="علت فراموشی یا تصحیح"
                type="text"
                value={corrReason}
                onChange={(e) => {
                  setCorrReason(e.target.value);
                }}
                placeholder="مثال: عدم همراه داشتن کارت هنگام خروج"
              />

              <ErrorLine error={proposeCorrectionAction.error} />
            </div>
            <footer className="modal-footer row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowCorrectionModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!corrReason.trim() || proposeCorrectionAction.busy}
                onClick={() => {
                  proposeCorrectionAction.run(undefined);
                }}
              >
                ارسال درخواست به مدیر
              </Button>
            </footer>
          </div>
        </div>
      )}

      {/* Modal: Record Leave */}
      {showLeaveModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: "450px" }}>
            <header className="modal-header">
              <h3>ثبت مرخصی پرسنل</h3>
            </header>
            <div className="modal-body stack" style={{ gap: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="leave-person-select" className="label">
                  پرسنل
                </label>
                <select
                  id="leave-person-select"
                  className="input"
                  value={leavePersonId}
                  onChange={(e) => {
                    setLeavePersonId(e.target.value);
                  }}
                >
                  {staff.map((s: PersonnelFileRow) => (
                    <option key={s.personId} value={s.personId}>
                      {s.personName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="leave-kind-select" className="label">
                  نوع مرخصی
                </label>
                <select
                  id="leave-kind-select"
                  className="input"
                  value={leaveKind}
                  onChange={(e) => {
                    setLeaveKind(e.target.value as "daily" | "hourly" | "sick" | "unpaid" | "other");
                  }}
                >
                  <option value="daily">استحقاقی روزانه</option>
                  <option value="hourly">استحقاقی ساعتی</option>
                  <option value="sick">استعلاجی</option>
                  <option value="unpaid">بدون حقوق</option>
                  <option value="other">سایر</option>
                </select>
              </div>

              <div className="row" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1 }}>
                  <Field
                    id="leave-start-date"
                    label="از تاریخ"
                    type="date"
                    value={leaveStartDate}
                    onChange={(e) => {
                      setLeaveStartDate(e.target.value);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Field
                    id="leave-end-date"
                    label="تا تاریخ"
                    type="date"
                    value={leaveEndDate}
                    onChange={(e) => {
                      setLeaveEndDate(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="leave-replacement-select" className="label">
                  همکار جایگزین شیفت (اختیاری)
                </label>
                <select
                  id="leave-replacement-select"
                  className="input"
                  value={leaveReplacementId}
                  onChange={(e) => {
                    setLeaveReplacementId(e.target.value);
                  }}
                >
                  <option value="">بدون جایگزین</option>
                  {staff
                    .filter((s: PersonnelFileRow) => s.personId !== leavePersonId)
                    .map((s: PersonnelFileRow) => (
                      <option key={s.personId} value={s.personId}>
                        {s.personName}
                      </option>
                    ))}
                </select>
              </div>

              <Field
                id="leave-reason-input"
                label="توضیحات / دلیل"
                type="text"
                value={leaveReason}
                onChange={(e) => {
                  setLeaveReason(e.target.value);
                }}
                placeholder="علت مرخصی"
              />

              <ErrorLine error={recordLeaveAction.error} />
            </div>
            <footer className="modal-footer row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowLeaveModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!leavePersonId || recordLeaveAction.busy}
                onClick={() => {
                  recordLeaveAction.run(undefined);
                }}
              >
                ثبت مرخصی
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
